import { Component, OnInit, QueryList, ViewChildren, Input, NgZone, OnDestroy } from '@angular/core';
import {
  SortableTableHeaderDirective,
  SortEvent, compare, pagingDefaultSettings
} from '../../../../shared/directives/sortable-table-header.directive';
import { Router, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ToasterService, LoadingOverlayService } from '../../../../shared/services';
import { TreatmentService } from '../../../../shared/services/treatment.service';
import { maxBy, countBy, isEmpty, cloneDeep, isEqual, find } from 'lodash';
import * as  moment from 'moment';
import { UserAuthService } from '../../../../shared/services/user-auth.service';
import { UserRolesService } from '../../../../shared/services/user-roles.service';
import { NbDialogService } from '@nebular/theme';
import { ModalConfirmComponent } from '../../../../shared/components/modals/confirm/modal-confirm.component';
import { ITreatmentTableItem } from './upload-treatment-table.model';
import { CPATTERN, ENUM_THEMES, EXPIRED_TIME_CHECK_UPLOAD_DATA, INTERVAL_CHECK_UPLOAD_DATA } from '../../../../shared/helpers/app.constants';
import { FileUploader } from 'ng2-file-upload';
import { ThemesSettingsService } from '../../../../shared/services/themes-settings.service';

const LIMIT_TREATMENTS = 100;
const MAX_TREATMENT_NUMBER = 9999;

@Component({
  selector: 'app-upload-treatment-table',
  templateUrl: './upload-treatment-table.component.html',
  styleUrls: ['./upload-treatment-table.component.scss']
})
export class UploadTreatmentTableComponent implements OnInit, OnDestroy {
  isDarkTheme: boolean;
  intervelTimer: {link: string, remaining: number, timer: any}[] = [];

  @Input() dataSource: ITreatmentTableItem[] = [];

  uploader: FileUploader = new FileUploader({});
  isFieldEngineer: boolean = false;

  queryParams = null;
  isLoadingData: boolean = false;
  isWarnBaseJobDataTime: boolean = false;

  editableFields = ['uidTreatmentInterval', 'baseTreatmentDateTime'];

  pagingState = { ...pagingDefaultSettings };
  isUploadAll: boolean = false;

  @ViewChildren(SortableTableHeaderDirective) headers: QueryList<SortableTableHeaderDirective>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private treatmentService: TreatmentService,
    private toasterService: ToasterService,
    private loadingOverlayService: LoadingOverlayService,
    private userAuthService: UserAuthService,
    private userRolesService: UserRolesService,
    private dialogService: NbDialogService,
    private themesSettingsService: ThemesSettingsService
  ) {
    const userInfo = this.userAuthService.getUserInfo();
    const userRole = this.userRolesService.toUserRole(userInfo);
    if (userRole.toLowerCase() === 'field engineer') {
      this.isFieldEngineer = true;
    }
  }

  get dataTable(): any[] {
    const result = this.dataSource
      .slice(
        (this.pagingState.page - 1) * this.pagingState.pageSize,
        (this.pagingState.page - 1) * this.pagingState.pageSize + this.pagingState.pageSize
      );

    if (!isEmpty(result)) {
      this.isWarnBaseJobDataTime = false;
      for (let index = 0; index < result.length; index++) {
        const item = result[index];
        if (item && this.isDefaultEpochTime(item.baseTreatmentDateTime)) {
          this.isWarnBaseJobDataTime = true;
          break;
        }
      }
    }

    return result;
  }

  private onCheckDarkThemes(): void {
    const localData = this.themesSettingsService.getLocalStorage();
    if (localData && localData.themes === ENUM_THEMES.Dark) {
      this.isDarkTheme = true;
      if (document.body.classList.value.toString().indexOf('frac-dark-theme') === -1) document.body.classList.add('frac-dark-theme');
    } else {
      this.isDarkTheme = false;
      document.body.classList.remove('frac-dark-theme');
    }
  }

  ngOnInit() {
    this.onCheckDarkThemes();
    this.route.queryParams.pipe(filter(params => params.wellId || params.noTreatmentsUpload || params.jobStartDate)).subscribe(param => {
      this.queryParams = {...param};
      this.loadListTreatments(this.queryParams);
    });
  }

  ngOnDestroy(): void {
    this.onDestroyInterval();
  }

  onSort({ column, direction }: SortEvent) {
    // resetting other headers
    this.headers.forEach(header => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });

    // sorting data
    if (direction === '') {
      this.dataSource = this.dataSource;
    } else {
      this.dataSource = [...this.dataSource].sort((a, b) => {
        const res = compare(a[column], b[column]);
        return direction === 'asc' ? res : -res;
      });
    }
  }

  loadListTreatments(param) {
    const { wellId, noTreatmentsUpload, jobStartDate } = param;
    if (wellId) {
      // get list created treatments and auto generate new unsave treatments for uploading if there is valid noTreatmentsUpload
      this.isLoadingData = true;
      this.treatmentService.getListTreatments(wellId).subscribe(response => {
        let listTreatments: ITreatmentTableItem[] = [];
        let countTreatmentStatus = null;
        if (response && response.result) {
          listTreatments = response.result;
          // map base job time
          listTreatments.forEach(item => {
            item.baseTreatmentDateTime = moment(item.baseTreatmentDateTime * 1000).isValid() ? moment(item.baseTreatmentDateTime * 1000).toDate() : new Date();
          });
          countTreatmentStatus = countBy(listTreatments, 'uploaded');
        }
        const countTreatmentsManualUpload = Math.floor(noTreatmentsUpload);
        let dataLength = countTreatmentsManualUpload;
        const createdTreatmentsLength = listTreatments.length;
        // in case there are some treatments created but still not upload csv
        if (!isEmpty(countTreatmentStatus) && countTreatmentStatus.false > 0) {
          dataLength = dataLength - countTreatmentStatus.false;
        }
        if (dataLength > 0) {
          let startTreatmentNumber = 0;
          // in case sum of no.treatments wanna upload and no.treatments created larger than LIMIT
          if (dataLength + createdTreatmentsLength > LIMIT_TREATMENTS) {
            dataLength = LIMIT_TREATMENTS - createdTreatmentsLength;
          }
          const largestTreatmentNumberItem = maxBy(listTreatments, (item) => item.uidTreatmentInterval);
          if (largestTreatmentNumberItem && largestTreatmentNumberItem.uidTreatmentInterval) {
            startTreatmentNumber = largestTreatmentNumberItem.uidTreatmentInterval;
          }
          for (let index = 0; index < dataLength; index++) {
            const item = {
              id: null,
              uidTreatmentInterval: startTreatmentNumber + index + 1,
              baseTreatmentDateTime: moment(jobStartDate).isValid() ? moment(jobStartDate).toDate() : new Date(),
              uploaded: false
            };
            listTreatments.push(item);
          }
        }
        // sort by treatment number
        this.dataSource = listTreatments.sort((a, b) => {
          return a.uidTreatmentInterval - b.uidTreatmentInterval;
        });
        this.pagingState.collectionSize = this.dataSource.length;
        this.isLoadingData = false;
      }, err => {
        this.isLoadingData = false;
      });
    }
  }

  uploadTreatmentFile(event: Event, element, treatment) {
    event.stopPropagation();
    event.preventDefault();
    if (element) {
      element.click();
    }
  }

  multiFileSelectChange(files) {
    // stage ie: Stage 1, Stg 1, S 1, Stage #1, Stg #1, S #1, Stage_1, Stg_1
    if (files && !isEmpty(files)) {
      console.log(files);
      this.dataSource = this.dataSource.map(dataItem => {
        const matchedFile = find(files, (file => {
          let fileName = file.name;
          let seperatedSplitArr = fileName.split('-');
          let stageStr = seperatedSplitArr.find(str => {
            return !!str.match(CPATTERN.MATCH_STAGE_STR);
          });
          if (stageStr) {
            let foundNumber;
            let firstSplitArr = stageStr.split(CPATTERN.MATCH_STAGE_STR);
            firstSplitArr.forEach(str => {
              const secondSplitArr = str.split(/([0-9]*)/);
              let number = secondSplitArr.find(n => !isNaN(parseInt(n)));
              number = parseInt(number);
              if(!isNaN(number)) {
                foundNumber = number;
              };
            });
            return dataItem.uidTreatmentInterval === foundNumber;
          }
          return false;
        }));
        if (matchedFile && !dataItem.selectedFile && !dataItem.uploaded) {
          dataItem.selectedFile = matchedFile;
        }
        return dataItem;
      });
    }
  }

  setTreatmentFile(event, element) {
    event.stopPropagation();
    event.preventDefault();
    if (element) {
      element.value = null;
      element.click();
    }
  }

  onDeleteSelectedFile(treatment) {
    if (treatment) {
      treatment.selectedFile = null;
    }
  }

  uploadSingleFile(event, treatment) {
    if (treatment && treatment.selectedFile) {
      this.promiseHandleUploadTreatmentFile(treatment.selectedFile, treatment).then(res => {
        treatment.selectedFile = null;
      });
    }
  }

  uploadAll() {
    const availableItems = this.dataSource.filter(treatment => treatment && treatment.selectedFile);
    const promises = availableItems.map(treatment => {
      return this.promiseHandleUploadTreatmentFile(treatment.selectedFile, treatment);
    });
    this.isUploadAll = true;
    Promise.all(promises).then(res => {
      this.isUploadAll = false;
      this.dataSource.forEach(treatment => {
        treatment.selectedFile = null;
      });
    }).catch(err => {
      this.isUploadAll = false;
    });
  }

  singleFileSelectChange(event, treatment, element) {
    if (event && event.target.files && treatment) {
      const selectedFile = event.target.files[0];
      treatment.selectedFile = selectedFile;
    }
  }

  promiseHandleUploadTreatmentFile(selectedFile, treatment) {
    return new Promise((resolve, reject) => {
      if (selectedFile && treatment) {
        if (this.queryParams && selectedFile) {
          treatment.isUploading = true;
          // handle case add manual well and have to manual create treatment
          const params = {
            wellId: this.queryParams.wellId,
            treatmentId: treatment.id
          };
          if (this.queryParams.noTreatmentsUpload * 1 > 0 && !treatment.id) {
            this.treatmentService.createTreatmentManual(this.queryParams.wellId, {
              treatmentNumber: treatment.uidTreatmentInterval,
              jobStartDate: Math.floor(new Date(treatment.baseTreatmentDateTime).getTime() / 1000)
            }).toPromise()
              .then(res => {
                if (res && res.result) {
                  params.treatmentId = res.result;
                  treatment.id = res.result;
                  this.treatmentService.uploadTreatmentFile(selectedFile, params).toPromise()
                    .then(response => {
                      // check data status is available to view
                      if (response && response.result && response.result.link) {
                        this.intervalCheckDataStatus(response.result.link, () => {
                          // update query param noTreatmentUpload when uploaded a csv succeed
                          treatment.isUploading = false;
                          treatment.uploaded = true;
                          if (selectedFile && selectedFile.name) {
                            treatment.csvFileName = selectedFile.name;
                          }
                          this.toasterService.showSuccess('Uploaded Successfully');
                        
                        }, () => {
                          treatment.isUploading = false;
                          treatment.uploaded = false;
                          selectedFile = null;
                          this.toasterService.showError('The file you are trying to upload is invalid format type. Please try again');
                        });
                      } else {
                        // update query param noTreatmentUpload when uploaded a csv succeed
                        this.updateQueryParam();
                        treatment.isUploading = false;
                        treatment.uploaded = true;
                        if (selectedFile && selectedFile.name) {
                          treatment.csvFileName = selectedFile.name;
                        }
                        this.toasterService.showSuccess('Uploaded Successfully');
                      }
                      resolve(true);
                    }, (err) => {
                      treatment.isUploading = false;
                      resolve(false);
                    });
                } else {
                  resolve(false);
                }
              })
              .catch(err => err);
          } else {
            // case uploaded XML and created treatment
            this.treatmentService.uploadTreatmentFile(selectedFile, params).toPromise()
              .then(response => {
                // check data status is available to view
                if (response && response.result && response.result.link) {
                  this.intervalCheckDataStatus(response.result.link, () => {
                    // update query param noTreatmentUpload when uploaded a csv succeed
                    treatment.isUploading = false;
                    treatment.uploaded = true;
                    if (selectedFile && selectedFile.name) {
                      treatment.csvFileName = selectedFile.name;
                    }
                    this.toasterService.showSuccess('Uploaded Successfully');
                  
                  }, () => {
                    treatment.isUploading = false;
                    treatment.uploaded = false;
                    selectedFile = null;
                    this.toasterService.showError('The file you are trying to upload is invalid format type. Please try again');
                  });
                } else {
                  treatment.isUploading = false;
                  treatment.uploaded = true;
                  if (selectedFile && selectedFile.name) {
                    treatment.csvFileName = selectedFile.name;
                  }
                  this.toasterService.showSuccess('Uploaded Successfully');
                }
                resolve(true);
              }).catch((err) => {
                treatment.isUploading = false;
                resolve(false);
              });
          }
        } else {
          resolve(false);
        }
      } else {
        resolve(false);
      }
    });
  }

  private onDestroyInterval(): void {
    this.intervelTimer.forEach(timer => clearInterval(timer.timer));
    this.intervelTimer = [];
  }

  private intervalCheckDataStatus(link, callback, errorHandler?) {
    // check data status is available to view
    if (link) {
      // run interval timer check data status is available to view
      let remainingIntervelTimer: number;
      const intervalCheckExistData = setInterval(() => {
        const currentTime = new Date().getTime();
        const index = this.intervelTimer.findIndex(timer => timer.link === link);
        if (index === -1) {
          remainingIntervelTimer = currentTime;
          this.intervelTimer.push({link: link, remaining: remainingIntervelTimer, timer: intervalCheckExistData});
        }
        else if (this.intervelTimer[index].remaining) remainingIntervelTimer = this.intervelTimer[index].remaining;
        else this.intervelTimer[index].remaining = remainingIntervelTimer = currentTime;

        if (currentTime - remainingIntervelTimer < EXPIRED_TIME_CHECK_UPLOAD_DATA) {
          this.treatmentService.checkDataStatus(link).subscribe(res => {
            // data is available to view
            if (res && res.result && res.result.success) {
              if (typeof callback === 'function') {
                callback();
              }
              clearInterval(intervalCheckExistData);
            }
          }, err => {
            clearInterval(intervalCheckExistData);
          });
        } else {
          if (typeof errorHandler === 'function') errorHandler();
          clearInterval(intervalCheckExistData);
        }
      }, INTERVAL_CHECK_UPLOAD_DATA);

    }
  }

  showTreatmentDetail(event: Event, treatment, index) {
    event.stopPropagation();
    if (treatment && this.queryParams.wellId && treatment.id) {
      this.treatmentService.getTreatment(this.queryParams.wellId, treatment.id, 'false').subscribe(response => {
        if (response && !isEmpty(response.result)) {
          this.router.navigate(['pages', 'treatments', 'uploadTreatment', 'detail'], {
            queryParams: {
              wellId: this.queryParams.wellId,
              wellName: this.queryParams.wellName,
              treatmentId: treatment.id,
              uidTreatmentInterval: treatment.uidTreatmentInterval,
            }
          });
        } else this.toasterService.showError('Please upload data for this treatment.');
      });
    } else {
      this.toasterService.showError('Please upload data for this treatment.');
    }
  }

  onDeleteConfirm(item): void {
    this.dialogService.open(ModalConfirmComponent, {
      context: {
        config: {
          title: 'Delete',
          message: `Are you sure you want to delete <br> <b class="h6">${item.csvFileName || 'this file'}</b>?`,
          className: 'pop-delete common-page',
          btnTxtConfirm: 'Yes',
          btnTxtCancel: 'No',
          data: item,
        }
      }
    }).onClose.subscribe(res => {
      if (res.confirm) {
        this.deleteTreatmentFile(res.data);
      }
    });
  }

  deleteTreatmentFile(treatment) {
    if (treatment && this.queryParams.wellId && treatment.id) {
      treatment.isUploading = true;
      this.treatmentService.deleteTreatmentFile(this.queryParams.wellId, treatment.id, treatment.csvFileName).subscribe(response => {
        treatment.isUploading = false;
        if (response && response.success) {
          treatment.uploaded = false;
        }
      }, (error) => {
        treatment.isUploading = false;
        treatment.uploaded = false;
      });
    } else {
      this.toasterService.showError('Please upload data for this treatment.');
    }
  }

  showEdit(event: Event, record) {
    if (record) {
      event.stopPropagation();
      record.isEditing = true;
      // init new value
      this.editableFields.forEach(keyProp => {
        record[keyProp + 'NewValue'] = record[keyProp];
      });
    }
  }

  saveInlineEdit(event: Event, record) {
    if (record) {
      const originValue = cloneDeep(record);
      event.stopPropagation();
      // set current value
      let isAllValid = true;
      this.editableFields.forEach(keyProp => {
        const newValue = record[keyProp + 'NewValue'];
        const currentValue = record[keyProp];
        // check is duplicate treatment number
        if (keyProp === 'uidTreatmentInterval') {
          if (this.isValidTreatmentNumber(newValue)) {
            if (newValue !== currentValue) {
              const treatmentNumberExists = this.dataTable.filter(item => item.uidTreatmentInterval === newValue);
              if (treatmentNumberExists.length > 0) {
                this.toasterService.showError(`Treatment number ${newValue} already existed`);
                isAllValid = false;
              }
            }
          } else {
            isAllValid = false;
            if (!newValue && newValue !== 0) {
              this.toasterService.showError(`Treatment number is required`);
            } else {
              this.toasterService.showError(`Treatment number must be between 1 and ${MAX_TREATMENT_NUMBER}`);
            }
          }
        }

        if (isAllValid) {
          record[keyProp] = newValue;
        }
      });
      if (isAllValid) {
        if (!isEqual(originValue, record)) {
          this.handleUpsertTreatment(record, this.queryParams);
        }
        record.isEditing = false;
      }
    }
  }

  cancelInlineEdit(event: Event, record, keyProp) {
    if (record) {
      event.stopPropagation();
      record.isEditing = false;
    }
  }

  isValidTreatmentNumber(value) {
    return typeof value === 'number' && !isNaN(value) && (value > 0 && value <= MAX_TREATMENT_NUMBER);
  }

  handleUpsertTreatment(record, queryParamsUrl) {
    const { wellId } = queryParamsUrl;
    if (wellId) {
      this.loadingOverlayService.show();
      if (record && record.id) {
        const queryParam = { wellId, treatmentId: record.id };
        const dataParam = {
          treatmentNumber: record.uidTreatmentInterval,
          jobStartDate: Math.floor(new Date(record.baseTreatmentDateTime).getTime() / 1000)
        };
        this.treatmentService.updateTreatment(queryParam, dataParam, () => {
          this.loadingOverlayService.hide();
        }).subscribe(res => {
        }, err => err);
      } else {
        this.treatmentService.createTreatmentManual(wellId, {
          treatmentNumber: record.uidTreatmentInterval,
          jobStartDate: Math.floor(new Date(record.baseTreatmentDateTime).getTime() / 1000)
        }, () => {
          this.loadingOverlayService.hide();
        }).subscribe(res => {
          // set id for treatment
          if (res && res.result) {
            record.id = res.result;
          }
        }, err => err);
      }
    }
  }

  updateQueryParam() {
    if (this.queryParams.noTreatmentsUpload && this.queryParams.noTreatmentsUpload > 0) {
      this.queryParams.noTreatmentsUpload = this.queryParams.noTreatmentsUpload - 1;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {...this.queryParams},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  changeWellSummary() {
    this.router.navigate(['pages/wells', this.queryParams.wellId]);
  }

  isDefaultEpochTime(datetime) {
    let result = false;
    if (datetime) {
      result = new Date(datetime).getTime() === 0;
    } else {
      result = true;
    }
    return result;
  }

}
